import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ArtworkIcon from "src/assets/all-icons/artwork-icon.jsx";

import ArtlistDataContext from '@/context/ArtlistDataContext';
import useTranslations from '@/i18n/useTranslations';
import ArtlistImage from '@/shared-components/cards/components/ArtlistImage';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';

import Options from '../artlist-body/options';

import './styles/ArtlistHeader.css';

export default function ArtlistHeader({ openAddArtworks, setOpenAddArtworks, autoOpenTimeSelector = false }) {
  const { artlistData } = useContext(ArtlistDataContext);
  const navigate = useNavigate();
  const t = useTranslations('common.profile.pages.artlist');

  const title = artlistData.artlist_title || t('header.defaults.artlist_title');
  const creator = artlistData.artlist_creator ?? {};
  const creatorName = creator.user_username || t('header.defaults.creator_name');
  const totalArtworks = artlistData.artlist_artworks_total ?? artlistData.artlist_artworks?.length ?? 0;
  const altArtlistImage = t('header.alt_image', {
    title,
    creatorName,
    count: totalArtworks || t('header.defaults.artworks_count'),
  });
  const likesCount = Number(artlistData.artlist_likes) || 0;
  const showLikes = likesCount > 0;
  const canNavigateToCreator = Boolean(creator.user_username);

  return (
    <div className="artlist-header__container">
      <ArtlistImage
        className="artlist-header__artlist-image"
        alt={altArtlistImage}
        image={artlistData.artlist_image?.image_id}
        style={{ viewTransitionName: `artlist_${artlistData._id}_animation` }}
      />

      <div className="artlist-header__info-section">
        <h1 className="artlist-header__artlist-title">
          {title}
        </h1>

        {Boolean(artlistData.artlist_description) && (
          <Text className="artlist-header__artlist-description" paragraph>
            {artlistData.artlist_description}
          </Text>
        )}

        <div className="artlist-header__metadata-actions">
          <div className="artlist-header__metadata">
            <button
              type="button"
              className="artlist-header__creator-info"
              onClick={() => navigate(`/user/${creator.user_username}`, { state: { from: true } })}
              disabled={!canNavigateToCreator}
            >
              <UserProfileImage
                className="artlist-header__creator-image"
                typeOfProfile={creator.user_type}
                image={creator.user_image?.image_id}
              />

              <Text className="artlist-header__creator-username" paragraph>
                {creatorName}
              </Text>
            </button>

            <div className="artlist-header__artlist-stats">
              <ArtworkIcon className="artlist-header__artwork-icon" />

              <Text className="artlist-header__artlist-stats-text">
                {t('header.stats.artworks', {
                  count: Utils.numberParserMillionThousand(totalArtworks),
                })}
              </Text>

              {showLikes && <div className="artlist-header__artlist-stats-separator" />}

              {showLikes && (
                <Text className="artlist-header__artlist-stats-text">
                  {t(`header.stats.likes.${likesCount === 1 ? 'one' : 'other'}`, {
                    count: Utils.numberParserMillionThousand(likesCount),
                  })}
                </Text>
              )}
            </div>
          </div>

          <Options
            openAddArtworks={openAddArtworks}
            setOpenAddArtworks={setOpenAddArtworks}
            autoOpenTimeSelector={autoOpenTimeSelector}
          />
        </div>
      </div>
    </div>
  );
}
